import { useCallback, useEffect, useRef, useState } from "react";
import type {
  DistributionPoint,
  MonthlyPoint,
  SalesCustomer,
  SalesOrder,
  SalesProduct,
} from "../components/SalesProductAnalyticsTabsDashboard.data";
import i18n from "src/i18n/config";
import {
  fetchShopifySalesProductCustomersSummary,
  fetchShopifySalesProductCustomersAll,
  fetchShopifySalesProductFinancialsByChannel,
  fetchShopifySalesProductFinancialsMonthlyTrend,
  fetchShopifySalesProductFinancialsPnl,
  fetchShopifySalesProductFinancialsSummary,
  fetchShopifySalesProductInventoryCategories,
  fetchShopifySalesProductInventoryProductsDetail,
  fetchShopifySalesProductInventorySummary,
  fetchShopifySalesProductOverview,
  fetchShopifySalesProductSalesCards,
  fetchShopifySalesProductSalesByChannel,
  fetchShopifySalesProductSalesOrderDetail,
  fetchShopifySalesProductSalesOrders,
  fetchShopifySalesProductSalesSummary,
  type ShopifySalesProductCustomersSort,
  type ShopifySalesProductInventorySort,
  type ShopifySalesProductOrdersSort,
} from "src/services/ShopifySalesProductAPI";
import type { ApiLang } from "src/utils/apiLang";
import {
  normalizeCustomersSummaryResponse,
  normalizeCustomersAllResponse,
  normalizeFinancialsMonthlyTrendResponse,
  normalizeFinancialsSummaryToFinancialBlock,
  normalizeInventoryCategoriesResponse,
  normalizeInventoryProductsDetailResponse,
  normalizeInventorySummaryResponse,
  normalizeSalesCardsResponse,
  normalizeSalesByChannelForChart,
  normalizeSalesOrderDetailResponse,
  normalizeSalesOrdersResponse,
  normalizeSalesProductOverviewResponse,
  normalizeSalesSummaryToFinancial,
  type CustomersSummaryView,
  type InventorySummaryMetrics,
  type SalesProductOverviewView,
  type SalesProductOrderDetailView,
  type SalesProductSalesCardsView,
} from "../utils/salesProductApiNormalize";

export function getOptionalTenantId(): number | undefined {
  const raw = typeof localStorage !== "undefined" ? localStorage.getItem("tenant_id") : null;
  if (!raw) {
    return undefined;
  }
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : undefined;
}

function hasRequiredDateRange(startDate: string, endDate: string): boolean {
  return Boolean(startDate?.trim() && endDate?.trim());
}

const SALES_PRODUCT_DATA_UNAVAILABLE =
  "This data is temporarily unavailable. Please try again later.";

function friendlySalesProductError(fallback = SALES_PRODUCT_DATA_UNAVAILABLE): string {
  return fallback;
}

function useLatestRequestGuard() {
  const idRef = useRef(0);
  const next = useCallback(() => ++idRef.current, []);
  const isCurrent = useCallback((id: number) => id === idRef.current, []);
  return { next, isCurrent };
}

export function useShopifySalesProductOverview(
  startDate: string,
  endDate: string,
  lang: ApiLang,
  tenantId?: number,
) {
  const [data, setData] = useState<SalesProductOverviewView | null>(null);
  const [rawData, setRawData] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { next, isCurrent } = useLatestRequestGuard();

  const fetchData = useCallback(async () => {
    const requestId = next();
    if (!hasRequiredDateRange(startDate, endDate)) {
      return;
    }
    setLoading(true);
    setError(null);
    setRawData(null);
    try {
      const raw = await fetchShopifySalesProductOverview(startDate, endDate, {
        tenantId,
        lang,
      });
      if (!isCurrent(requestId)) {return;}
      setRawData(raw);
      setData(normalizeSalesProductOverviewResponse(raw));
    } catch (err) {
      if (!isCurrent(requestId)) {return;}
      setError(
        err instanceof Error ? err.message : i18n.t("shopifyGeo.errors.loadFailed"),
      );
      setRawData(null);
      setData(null);
    } finally {
      if (isCurrent(requestId)) {setLoading(false);}
    }
  }, [startDate, endDate, lang, tenantId, next, isCurrent]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, rawData, loading, error, refetch: fetchData };
}

export function useShopifySalesProductSalesByChannel(
  startDate: string,
  endDate: string,
  lang: ApiLang,
  tenantId?: number,
  status: string = "all",
  metric: "sales" | "orders" = "sales",
) {
  const [channels, setChannels] = useState<DistributionPoint[]>([]);
  const [rawData, setRawData] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { next, isCurrent } = useLatestRequestGuard();

  const fetchData = useCallback(async () => {
    const requestId = next();
    if (!hasRequiredDateRange(startDate, endDate)) {
      return;
    }
    setLoading(true);
    setError(null);
    setRawData(null);
    try {
      const raw = await fetchShopifySalesProductSalesByChannel(startDate, endDate, {
        tenantId,
        lang,
        status,
        metric,
      });
      if (!isCurrent(requestId)) {return;}
      setRawData(raw);
      setChannels(normalizeSalesByChannelForChart(raw, metric));
    } catch (err) {
      if (!isCurrent(requestId)) {return;}
      setError(err instanceof Error ? err.message : "Failed to load channel data");
      setRawData(null);
      setChannels([]);
    } finally {
      if (isCurrent(requestId)) {setLoading(false);}
    }
  }, [startDate, endDate, lang, tenantId, status, metric, next, isCurrent]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { channels, rawData, loading, error, refetch: fetchData };
}

export function useShopifySalesProductSalesCards(
  startDate: string,
  endDate: string,
  lang: ApiLang,
  tenantId?: number,
) {
  const [cards, setCards] = useState<SalesProductSalesCardsView | null>(null);
  const [rawData, setRawData] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { next, isCurrent } = useLatestRequestGuard();

  const fetchData = useCallback(async () => {
    const requestId = next();
    if (!hasRequiredDateRange(startDate, endDate)) {
      return;
    }
    setLoading(true);
    setError(null);
    setRawData(null);
    try {
      const raw = await fetchShopifySalesProductSalesCards(startDate, endDate, {
        tenantId,
        lang,
      });
      if (!isCurrent(requestId)) {return;}
      setRawData(raw);
      setCards(normalizeSalesCardsResponse(raw));
    } catch (err) {
      if (!isCurrent(requestId)) {return;}
      setError(err instanceof Error ? err.message : "Failed to load sales cards");
      setRawData(null);
      setCards(null);
    } finally {
      if (isCurrent(requestId)) {setLoading(false);}
    }
  }, [startDate, endDate, lang, tenantId, next, isCurrent]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { cards, rawData, loading, error, refetch: fetchData };
}

export function useShopifySalesProductSalesSummary(
  startDate: string,
  endDate: string,
  lang: ApiLang,
  tenantId?: number,
) {
  const [financial, setFinancial] = useState<ReturnType<
    typeof normalizeSalesSummaryToFinancial
  >>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { next, isCurrent } = useLatestRequestGuard();

  const fetchData = useCallback(async () => {
    const requestId = next();
    if (!hasRequiredDateRange(startDate, endDate)) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const raw = await fetchShopifySalesProductSalesSummary(startDate, endDate, {
        tenantId,
        lang,
      });
      if (!isCurrent(requestId)) {return;}
      setFinancial(normalizeSalesSummaryToFinancial(raw));
    } catch (err) {
      if (!isCurrent(requestId)) {return;}
      setError(err instanceof Error ? err.message : "Failed to load sales summary");
      setFinancial(null);
    } finally {
      if (isCurrent(requestId)) {setLoading(false);}
    }
  }, [startDate, endDate, lang, tenantId, next, isCurrent]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { financial, loading, error, refetch: fetchData };
}

export function useShopifySalesProductOrders(
  startDate: string,
  endDate: string,
  lang: ApiLang,
  tenantId: number | undefined,
  page: number,
  pageSize: number,
  sort: ShopifySalesProductOrdersSort,
  search: string,
  status: string,
) {
  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [rawData, setRawData] = useState<unknown>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { next, isCurrent } = useLatestRequestGuard();

  const fetchData = useCallback(async () => {
    const requestId = next();
    if (!hasRequiredDateRange(startDate, endDate)) {
      return;
    }
    setLoading(true);
    setError(null);
    setRawData(null);
    try {
      const raw = await fetchShopifySalesProductSalesOrders(startDate, endDate, {
        tenantId,
        lang,
        page,
        pageSize,
        sort,
        search: search || undefined,
        status: status !== "all" ? status : undefined,
      });
      if (!isCurrent(requestId)) {return;}
      setRawData(raw);
      const normalized = normalizeSalesOrdersResponse(raw);
      setOrders(normalized.orders);
      setTotal(normalized.total);
    } catch (err) {
      if (!isCurrent(requestId)) {return;}
      setError(err instanceof Error ? err.message : "Failed to load orders");
      setRawData(null);
      setOrders([]);
      setTotal(0);
    } finally {
      if (isCurrent(requestId)) {setLoading(false);}
    }
  }, [startDate, endDate, lang, tenantId, page, pageSize, sort, search, status, next, isCurrent]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { orders, total, rawData, loading, error, refetch: fetchData };
}

export function useShopifySalesProductOrderDetail(
  orderId: string | null,
  lang: ApiLang,
  tenantId?: number,
) {
  const [order, setOrder] = useState<SalesProductOrderDetailView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { next, isCurrent } = useLatestRequestGuard();

  const fetchData = useCallback(async () => {
    const requestId = next();
    if (!orderId) {
      setOrder(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const raw = await fetchShopifySalesProductSalesOrderDetail(orderId, { tenantId, lang });
      if (!isCurrent(requestId)) {return;}
      setOrder(normalizeSalesOrderDetailResponse(raw));
    } catch (err) {
      if (!isCurrent(requestId)) {return;}
      setError(err instanceof Error ? err.message : "Failed to load order detail");
      setOrder(null);
    } finally {
      if (isCurrent(requestId)) {setLoading(false);}
    }
  }, [orderId, lang, tenantId, next, isCurrent]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { order, loading, error, refetch: fetchData };
}

const INVENTORY_PRODUCTS_PAGE_SIZE = 10;

export function useShopifySalesProductInventory(
  lang: ApiLang,
  tenantId: number | undefined,
  page: number,
  pageSize: number = INVENTORY_PRODUCTS_PAGE_SIZE,
  search: string = "",
  status: string | undefined = undefined,
  category: string | undefined = undefined,
  sort: ShopifySalesProductInventorySort = "stock_desc",
) {
  const [summary, setSummary] = useState<InventorySummaryMetrics | null>(null);
  const [products, setProducts] = useState<SalesProduct[]>([]);
  const [rawData, setRawData] = useState<{ summary: unknown | null; productsDetail: unknown | null }>({
    summary: null,
    productsDetail: null,
  });
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const { next, isCurrent } = useLatestRequestGuard();

  const fetchData = useCallback(async () => {
    const requestId = next();
    setLoading(true);
    setError(null);
    setSummaryError(null);
    setDetailError(null);
    setRawData({ summary: null, productsDetail: null });
    try {
      const [summaryResult, detailResult] = await Promise.allSettled([
        fetchShopifySalesProductInventorySummary({ tenantId, lang }),
        fetchShopifySalesProductInventoryProductsDetail({
          tenantId,
          lang,
          page,
          pageSize: Math.min(100, Math.max(1, pageSize)),
          search: search || undefined,
          status,
          category,
          sort,
        }),
      ]);
      if (!isCurrent(requestId)) {return;}

      const nextErrors: string[] = [];
      const nextRawData = {
        summary: summaryResult.status === "fulfilled" ? summaryResult.value : null,
        productsDetail: detailResult.status === "fulfilled" ? detailResult.value : null,
      };
      setRawData(nextRawData);
      if (summaryResult.status === "fulfilled") {
        setSummary(normalizeInventorySummaryResponse(summaryResult.value));
      } else {
        setSummary(null);
        const message = friendlySalesProductError(
          "Inventory summary is temporarily unavailable. Product insights below are still usable.",
        );
        setSummaryError(message);
        nextErrors.push(message);
      }

      if (detailResult.status === "fulfilled") {
        const detail = normalizeInventoryProductsDetailResponse(detailResult.value);
        setProducts(detail.products);
        setTotalProducts(detail.total);
        setTotalPages(detail.totalPages);
      } else {
        setProducts([]);
        setTotalProducts(0);
        setTotalPages(1);
        const message = friendlySalesProductError(
          "Product inventory details are temporarily unavailable. Summary metrics remain available when loaded.",
        );
        setDetailError(message);
        nextErrors.push(message);
      }

      setError(nextErrors.length ? nextErrors[0] : null);
    } catch {
      if (!isCurrent(requestId)) {return;}
      const message = friendlySalesProductError("Inventory data is temporarily unavailable. Please try again later.");
      setError(message);
      setSummaryError(message);
      setDetailError(message);
      setSummary(null);
      setRawData({ summary: null, productsDetail: null });
      setProducts([]);
      setTotalProducts(0);
      setTotalPages(1);
    } finally {
      if (isCurrent(requestId)) {setLoading(false);}
    }
  }, [lang, tenantId, page, pageSize, search, status, category, sort, next, isCurrent]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    summary,
    products,
    rawData,
    totalProducts,
    totalPages,
    loading,
    error,
    summaryError,
    detailError,
    refetch: fetchData,
  };
}

export function useShopifySalesProductTopInventoryProducts(
  lang: ApiLang,
  tenantId: number | undefined,
  search: string = "",
  status: string | undefined = undefined,
  category: string | undefined = undefined,
) {
  const [products, setProducts] = useState<SalesProduct[]>([]);
  const [rawData, setRawData] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setRawData(null);
    fetchShopifySalesProductInventoryProductsDetail({
      tenantId,
      lang,
      page: 1,
      pageSize: 10,
      search: search || undefined,
      status,
      category,
      sort: "stock_desc",
    })
      .then(raw => {
        if (!cancelled) {
          setRawData(raw);
          setProducts(normalizeInventoryProductsDetailResponse(raw).products);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRawData(null);
          setProducts([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [lang, tenantId, search, status, category]);

  return { products, rawData, loading };
}

export function useShopifySalesProductInventoryCategories(
  lang: ApiLang,
  tenantId: number | undefined,
) {
  const [categories, setCategories] = useState<string[]>([]);
  const [rawData, setRawData] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { next, isCurrent } = useLatestRequestGuard();

  const fetchData = useCallback(async () => {
    const requestId = next();
    setLoading(true);
    setError(null);
    setRawData(null);
    try {
      const raw = await fetchShopifySalesProductInventoryCategories({ tenantId, lang });
      if (!isCurrent(requestId)) {return;}
      setRawData(raw);
      setCategories(normalizeInventoryCategoriesResponse(raw));
    } catch (err) {
      if (!isCurrent(requestId)) {return;}
      setError(err instanceof Error ? err.message : "Failed to load inventory categories");
      setRawData(null);
      setCategories([]);
    } finally {
      if (isCurrent(requestId)) {setLoading(false);}
    }
  }, [lang, tenantId, next, isCurrent]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { categories, rawData, loading, error, refetch: fetchData };
}

export function useShopifySalesProductCustomers(
  startDate: string,
  endDate: string,
  lang: ApiLang,
  tenantId: number | undefined,
  page: number,
  pageSize: number,
  search: string,
  sort?: ShopifySalesProductCustomersSort,
) {
  const [summary, setSummary] = useState<CustomersSummaryView | null>(null);
  const [customers, setCustomers] = useState<SalesCustomer[]>([]);
  const [rawData, setRawData] = useState<{ summary: unknown | null; customers: unknown | null }>({
    summary: null,
    customers: null,
  });
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const { next, isCurrent } = useLatestRequestGuard();

  const fetchData = useCallback(async () => {
    const requestId = next();
    if (!hasRequiredDateRange(startDate, endDate)) {
      return;
    }
    setLoading(true);
    setError(null);
    setSummaryError(null);
    setListError(null);
    setRawData({ summary: null, customers: null });
    let nextError: string | null = null;
    try {
      const summaryRaw = await fetchShopifySalesProductCustomersSummary(startDate, endDate, { tenantId, lang });
      if (!isCurrent(requestId)) {return;}
      setRawData(current => ({ ...current, summary: summaryRaw }));
      setSummary(normalizeCustomersSummaryResponse(summaryRaw));
    } catch {
      if (!isCurrent(requestId)) {return;}
      setRawData(current => ({ ...current, summary: null }));
      setSummary(null);
      nextError = friendlySalesProductError(
        "Customer summary is temporarily unavailable. Customer list below is still usable.",
      );
      setSummaryError(nextError);
    }
    try {
      const customersRaw = await fetchShopifySalesProductCustomersAll(startDate, endDate, {
          tenantId,
          lang,
          page,
          pageSize,
          sort,
          search: search || undefined,
        });
      if (!isCurrent(requestId)) {return;}
      setRawData(current => ({ ...current, customers: customersRaw }));
      const normalizedCustomers = normalizeCustomersAllResponse(customersRaw);
      setCustomers(normalizedCustomers.customers);
      setTotalCustomers(normalizedCustomers.total);
      setTotalPages(normalizedCustomers.totalPages);
    } catch {
      if (!isCurrent(requestId)) {return;}
      const message = friendlySalesProductError(
        "Customer list is temporarily unavailable. Summary metrics remain available when loaded.",
      );
      nextError = nextError ?? message;
      setListError(message);
      setRawData(current => ({ ...current, customers: null }));
      setCustomers([]);
      setTotalCustomers(0);
      setTotalPages(1);
    } finally {
      if (isCurrent(requestId)) {
        setError(nextError);
        setLoading(false);
      }
    }
  }, [startDate, endDate, lang, tenantId, page, pageSize, search, sort, next, isCurrent]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    summary,
    customers,
    rawData,
    totalCustomers,
    totalPages,
    loading,
    error,
    summaryError,
    listError,
    refetch: fetchData,
  };
}

export async function fetchShopifySalesProductCustomersForExport(
  startDate: string,
  endDate: string,
  lang: ApiLang,
  tenantId: number | undefined,
  search: string,
  sort?: ShopifySalesProductCustomersSort,
  onProgress?: (_loaded: number, _total: number) => void,
) {
  const pageSize = 100;
  const first = normalizeCustomersAllResponse(await fetchShopifySalesProductCustomersAll(startDate, endDate, {
    tenantId,
    lang,
    page: 1,
    pageSize,
    sort,
    search: search || undefined,
  }));
  onProgress?.(first.customers.length, first.total);
  if (first.totalPages <= 1) {
    return first.customers;
  }
  const customers = [...first.customers];
  const pages = Array.from({ length: first.totalPages - 1 }, (_, index) => index + 2);
  for (let i = 0; i < pages.length; i += 6) {
    const batch = await Promise.all(
      pages.slice(i, i + 6).map(page =>
        fetchShopifySalesProductCustomersAll(startDate, endDate, {
          tenantId,
          lang,
          page,
          pageSize,
          sort,
          search: search || undefined,
        }).then(raw => normalizeCustomersAllResponse(raw).customers),
      ),
    );
    customers.push(...batch.flat());
    onProgress?.(customers.length, first.total);
  }
  return customers;
}

export type FinancialsBundle = {
  summary: {
    revenue: number;
    revenueChangePercent?: number | null;
    cogs: number;
    grossProfit: number;
    taxes: number;
    taxesChangePercent?: number | null;
    expenses: number;
    refunds: number;
    refundsChangePercent?: number | null;
    netProfit: number;
    netProfitChangePercent?: number | null;
    comparisonLabel?: string;
  };
  monthly: MonthlyPoint[];
  channelData: DistributionPoint[];
  pnlPayload: unknown;
};

export function useShopifySalesProductFinancials(
  startDate: string,
  endDate: string,
  lang: ApiLang,
  tenantId: number | undefined,
) {
  const [data, setData] = useState<FinancialsBundle | null>(null);
  const [rawData, setRawData] = useState<{
    summary: unknown;
    monthlyTrend: unknown;
    byChannel: unknown;
    pnl: unknown;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { next, isCurrent } = useLatestRequestGuard();

  const fetchData = useCallback(async () => {
    const requestId = next();
    if (!hasRequiredDateRange(startDate, endDate)) {
      return;
    }
    setLoading(true);
    setError(null);
    setRawData(null);
    try {
      const [summaryRaw, trendRaw, channelRaw, pnlRaw] = await Promise.all([
        fetchShopifySalesProductFinancialsSummary(startDate, endDate, { tenantId, lang }),
        fetchShopifySalesProductFinancialsMonthlyTrend(startDate, endDate, { tenantId, lang }),
        fetchShopifySalesProductFinancialsByChannel(startDate, endDate, { tenantId, lang }),
        fetchShopifySalesProductFinancialsPnl(startDate, endDate, { tenantId, lang }),
      ]);
      if (!isCurrent(requestId)) {return;}
      setRawData({
        summary: summaryRaw,
        monthlyTrend: trendRaw,
        byChannel: channelRaw,
        pnl: pnlRaw,
      });
      const summaryBlock = normalizeFinancialsSummaryToFinancialBlock(summaryRaw);
      setData({
        summary: summaryBlock,
        monthly: normalizeFinancialsMonthlyTrendResponse(trendRaw),
        channelData: normalizeSalesByChannelForChart(channelRaw),
        pnlPayload: pnlRaw,
      });
    } catch (err) {
      if (!isCurrent(requestId)) {return;}
      setError(err instanceof Error ? err.message : "Failed to load financials");
      setRawData(null);
      setData(null);
    } finally {
      if (isCurrent(requestId)) {setLoading(false);}
    }
  }, [startDate, endDate, lang, tenantId, next, isCurrent]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, rawData, loading, error, refetch: fetchData };
}
